import React, { useState } from 'react';
import { PhoneIncoming, Send, MessageSquare, Users, CalendarClock, ArrowRight, Check, Circle, AlertTriangle, Radio, Database, CalendarCheck, BellRing } from 'lucide-react';
import { useStepper } from './useStepper';
import { Controls } from './OpsSections';
import { applyNow } from './homeData';
import { OPS, STEPS, STEP_PANEL, STATUS_BY_STEP } from './opsScenarios';

const KIND = { call: PhoneIncoming, out: Send, in: MessageSquare, team: Users };
const KIND_LABEL = { call: 'Event', out: 'Automated', in: 'Customer', team: 'Team' };
const PRIO = { Urgent: 'red', High: 'amber', Medium: 'blue', Low: 'slate' };
const FINAL = { pending: ['Pending Confirmation', 'amber'], confirmed: ['Confirmed', 'green'], action: ['Action Required', 'red'] };
const SYS = [[Radio, 'Communication Connected', 1], [Database, 'CRM Activity', 3], [CalendarCheck, 'Scheduling Workflow', 4], [BellRing, 'Team Notifications', 5]];

const Pipeline = ({ sel, k, pick, on }) => (
  <div className={`yv-cc-panel yv-cc-pipe ${on ? 'is-on' : ''}`} data-testid="cc-pipeline">
    <div className="yv-cc-head"><span className="yv-mono">Lead Pipeline</span><span className="yv-mono yv-muted-ink">{OPS.length} Leads</span></div>
    {OPS.map((o) => {
      const isSel = o.id === sel.id;
      return (
        <button key={o.id} onClick={() => pick(o.id)} aria-pressed={isSel} className={`yv-cc-lead ${isSel ? 'is-sel' : ''}`} data-testid={`cc-lead-${o.id}`}>
          <span className="yv-cc-lead-top"><b className="yv-keep-case">{o.lead}</b><span className={`yv-cc-prio is-${PRIO[o.priority]}`}>{o.priority}</span></span>
          <span className="yv-cc-lead-need">{o.need}</span>
          <span className="yv-cc-lead-meta"><span>{o.source}</span><span className="yv-cc-st">{isSel ? STATUS_BY_STEP[k] : 'Team Notified'}</span></span>
          <span className="yv-cc-lead-meta yv-muted-ink"><span>{o.owner}</span><span>{o.last}</span></span>
        </button>
      );
    })}
  </div>
);

const Timeline = ({ sel, k, on }) => (
  <div className={`yv-cc-panel yv-cc-time ${on ? 'is-on' : ''}`} data-testid="cc-timeline">
    <div className="yv-cc-head"><span className="yv-mono">Conversation Timeline · {sel.lead}</span><span className="yv-mono yv-muted-ink">{sel.tab}</span></div>
    <ol className="yv-cc-events yv-keep-case" aria-live="polite">
      {sel.events.filter((e) => e.at <= k).map((e, i) => {
        const Icon = KIND[e.kind];
        return (
          <li key={`${sel.id}-${i}`} className={`yv-cc-ev is-${e.kind}`} data-testid={`cc-event-${i}`}>
            <span className="yv-cc-ev-meta"><Icon className="w-3.5 h-3.5" aria-hidden="true" />{[KIND_LABEL[e.kind], e.who].filter((v, j, a) => a.indexOf(v) === j).join(' · ')} · {e.time}</span>
            <p>{e.text}</p>
          </li>
        );
      })}
    </ol>
  </div>
);

const Row = ({ l, v }) => <div className="yv-cc-row"><dt>{l}</dt><dd className="yv-keep-case">{v}</dd></div>;

const Appointment = ({ sel, k, on }) => {
  const requested = k >= 4;
  const [label, tone] = k >= 5 ? FINAL[sel.appt.final] : requested ? ['Awaiting Team Review', 'amber'] : ['No Request Yet', 'slate'];
  return (
    <div className={`yv-cc-panel yv-cc-appt ${on ? 'is-on' : ''}`} data-testid="cc-appointment">
      <div className="yv-cc-head"><span className="yv-mono">Appointment & Next Action</span></div>
      <span className={`yv-cc-badge is-${tone}`} data-testid="cc-appt-status">{label}</span>
      <dl>
        <Row l="Requested Service" v={sel.appt.service} />
        <Row l="Scheduling Window" v={requested ? sel.appt.window : '—'} />
        <Row l="Assigned To" v={k >= 5 ? sel.owner : 'Unassigned'} />
      </dl>
      <div className={`yv-cc-next ${k >= 5 ? 'is-on' : ''}`} data-testid="cc-next-action">
        {sel.appt.final === 'action' && k >= 5 ? <AlertTriangle className="w-4 h-4" aria-hidden="true" /> : <ArrowRight className="w-4 h-4" aria-hidden="true" />}
        <div><b>Next Action</b><span className="yv-keep-case">{k >= 5 ? sel.appt.next : 'Waiting For Workflow To Complete'}</span></div>
      </div>
    </div>
  );
};

const Activity = ({ sel, k }) => (
  <div className="yv-cc-panel yv-cc-act" data-testid="cc-activity">
    <div className="yv-cc-head"><span className="yv-mono">Activity & System Status</span><span className="yv-mono yv-muted-ink">{Math.min(k + 1, 6)} Of 6 Complete</span></div>
    <ol>{sel.crm.map((c, i) => (
      <li key={c} className={i <= k ? 'done' : ''} data-testid={`cc-activity-${i}`}>
        {i <= k ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <Circle className="w-3.5 h-3.5" aria-hidden="true" />}
        <span><b>{STEPS[i]}</b>{c}</span>
      </li>
    ))}</ol>
  </div>
);

export const LeadWorkspace = () => {
  const [id, setId] = useState(OPS[0].id);
  const sel = OPS.find((o) => o.id === id);
  const s = useStepper(6, undefined, { delay: 1500 });
  const k = s.i;
  const pick = (x) => { setId(x); s.go(5); };
  const hot = s.playing ? STEP_PANEL[k] : null;
  return (
    <section id="interface" className="yv-section yv-dark yv-grid-dark" data-testid="interface-section">
      <div className="yv-container">
        <div className="yv-section-head yv-split-head">
          <div>
            <p className="yv-eyebrow">Connected Business Operations</p>
            <h2 className="yv-h2">Every Conversation. Every Opportunity. <span className="yv-blue-ink yv-block">One Connected System.</span></h2>
          </div>
          <p className="yv-lede">See How YOVAI Connects Customer Inquiries, Automated Follow-Up, Appointment Requests, CRM Activity, And Team Notifications Into One Coordinated Workflow.</p>
        </div>
        <div className="yv-cc" data-testid="command-center">
          <div className="yv-cc-top">
            <div className="yv-chiptabs" role="tablist" aria-label="Sample scenarios">
              {OPS.map((o) => <button key={o.id} role="tab" aria-selected={o.id === id} className={o.id === id ? 'on' : ''} onClick={() => pick(o.id)} data-testid={`cc-scenario-${o.id}`}>{o.tab} — {o.title}</button>)}
            </div>
            <span className="yv-tag yv-tag-dark" data-testid="cc-demo-label">Illustrative Demo Data · Fictional Customers</span>
          </div>
          <div className="yv-cc-bar">
            <ul className="yv-cc-sys" aria-label="Simulated system status">
              {SYS.map(([I, l, at]) => <li key={l} className={k >= at ? 'on' : ''} data-testid={`cc-sys-${at}`}><I className="w-3.5 h-3.5" aria-hidden="true" />{l}<span className="yv-mono">{k >= at ? 'Simulated · Active' : 'Waiting'}</span></li>)}
            </ul>
            <Controls s={s} label="cc-workflow" />
          </div>
          <ol className="yv-cc-rail" aria-label="Workflow steps">
            {STEPS.map((t, i) => <li key={t}><button onClick={() => s.go(i)} className={`${i < k ? 'done' : ''} ${i === k ? 'on' : ''}`} aria-current={i === k ? 'step' : undefined} data-testid={`cc-step-${i}`}><span className="yv-mono">{String(i + 1).padStart(2, '0')}</span>{t}</button></li>)}
          </ol>
          <div className="yv-cc-grid">
            <Pipeline sel={sel} k={k} pick={pick} on={hot === 'pipeline'} />
            <Timeline sel={sel} k={k} on={hot === 'timeline'} />
            <Appointment sel={sel} k={k} on={hot === 'appt'} />
          </div>
          <Activity sel={sel} k={k} />
        </div>
        <div className="yv-cc-cta" data-testid="cc-cta">
          <div><h3 className="yv-h3">Your Business Has Its Own Workflow. <span className="yv-blue-ink">We Build The Infrastructure Around It.</span></h3>
            <p>From Lead Recovery To Scheduling And CRM Integration, YOVAI Designs Systems Around Your Existing Operations And Business Requirements.</p></div>
          <button onClick={() => applyNow()} className="yv-btn yv-btn-primary" data-testid="cc-apply-btn">Apply For A Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
        </div>
      </div>
    </section>
  );
};
